import './ProjectListView.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Project } from 'src/modules/common/types/Project';
import { PROJECT_LIST_PAGE_SIZE } from 'src/modules/project-list/constants/pageSize';
import { ProjectListClonning } from 'src/modules/project-list/types/ProjectListClonning';
import { ProjectListColumn, ProjectListColumnKey } from 'src/modules/project-list/types/ProjectListColumn';
import { ProjectListDownloading } from 'src/modules/project-list/types/ProjectListDownloading';
import { ProjectListExporting } from 'src/modules/project-list/types/ProjectListExporting';
import { ProjectListQuery } from 'src/modules/project-list/types/ProjectListQuery';
import { ProjectListSlice } from 'src/modules/project-list/types/ProjectListSlice';
import { ProjectListViewMode } from 'src/modules/project-list/types/ProjectListViewMode';
import { ProjectListUnfollowing } from 'src/modules/project-list/types/ProjectUnfollowing';
import { ProjectListHeader } from 'src/modules/project-list/views/ProjectListHeader';
import { ProjectListItemGridView } from 'src/modules/project-list/views/ProjectListItemGridView';
import { ProjectListItemListView } from 'src/modules/project-list/views/ProjectListItemListView';
import { ProjectListPager } from 'src/modules/project-list/views/ProjectListPager';

type Props = {
  readonly query: ProjectListQuery;
  readonly projects: ProjectListSlice;
  readonly columns: ReadonlyArray<ProjectListColumn>;
  readonly downloading: ProjectListDownloading | null;
  readonly unfollowing: ProjectListUnfollowing | null;
  readonly clonning: ProjectListClonning | null;
  readonly exporting: ProjectListExporting | null;
  readonly onRemove: (project: Project) => void;
  readonly onDownload: (project: Project) => void;
  readonly onUnfollow: (project: Project) => void;
  readonly onShare: (project: Project) => void;
  readonly onClone: (project: Project) => void;
  readonly onExport: (project: Project) => void;
};

export const ProjectListView = ({
  query,
  projects,
  columns,
  downloading,
  unfollowing,
  clonning,
  exporting,
  onRemove,
  onDownload,
  onUnfollow,
  onShare,
  onClone,
  onExport,
}: Props): React.ReactElement => {
  const ref = useRef<HTMLDivElement>(null);

  const [scrollable, setScrollable] = useState(false);

  const handleResize = useCallback<ResizeObserverCallback>((entries) => {
    if (!Array.isArray(entries) || !entries.length) {
      return;
    }

    const entry = entries[0];

    setScrollable(Number(entry.target.scrollWidth) > Number(entry.target.clientWidth));
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) { return; }

    const observer = new ResizeObserver(handleResize);
    observer.observe(element);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [handleResize]);

  return (
    <div className="bp-project-list-view">
      <div ref={ref} className="bp-project-list-view__content">
        {query.view === ProjectListViewMode.LIST && (
          <div className="bp-project-list-view__table" style={getGridStyles(columns)}>
            <ProjectListHeader
              query={query}
              columns={columns}
              scrollable={scrollable}
            />

            {projects.found.map((listItem) => (
              <ProjectListItemListView
                key={listItem.project.id}
                item={listItem}
                columns={columns}
                scrollable={scrollable}
                downloading={(
                  downloading?.project.id === listItem.project.id &&
                downloading.status.state === 'pending'
                )}
                unfollowing={(
                  unfollowing?.project.id === listItem.project.id &&
                unfollowing?.status?.state === 'pending'
                )}
                clonning={(
                  clonning?.project.id === listItem.project.id &&
                clonning?.status?.state === 'pending'
                )}
                exporting={(
                  exporting?.project.id === listItem.project.id &&
                exporting?.status?.state === 'pending'
                )}
                onRemove={onRemove}
                onDownload={onDownload}
                onUnfollow={onUnfollow}
                onShare={onShare}
                onClone={onClone}
                onExport={onExport}
              />
            ))}
          </div>
        )}
        {query.view === ProjectListViewMode.GRID && (
          <div className="bp-project-list-view__grid">
            {projects.found.map((listItem) => (
              <ProjectListItemGridView
                key={listItem.project.id}
                item={listItem}
                columns={columns}
                downloading={(
                  downloading?.project.id === listItem.project.id &&
                downloading.status.state === 'pending'
                )}
                unfollowing={(
                  unfollowing?.project.id === listItem.project.id &&
                unfollowing?.status?.state === 'pending'
                )}
                clonning={(
                  clonning?.project.id === listItem.project.id &&
                clonning?.status?.state === 'pending'
                )}
                exporting={(
                  exporting?.project.id === listItem.project.id &&
                exporting?.status?.state === 'pending'
                )}
                onRemove={onRemove}
                onDownload={onDownload}
                onUnfollow={onUnfollow}
                onShare={onShare}
                onClone={onClone}
                onExport={onExport}
              />
            ))}
          </div>
        )}

        {projects.total === 0 && (
          <p className="bp-project-list-view__empty-text">
            <FormattedMessage id="projectList/noResults"/>
          </p>
        )}

        {projects.total > PROJECT_LIST_PAGE_SIZE[query.view] && (
          <div className="bp-project-list-view__pagination">
            <ProjectListPager
              total={projects.total}
              query={query}
            />
          </div>
        )}
      </div>
    </div>
  );
};

function getGridStyles(columns: ReadonlyArray<ProjectListColumn>): React.CSSProperties {
  return {
    gridTemplateColumns: [
      STATIC_COLUMNS_WIDTH.image,
      STATIC_COLUMNS_WIDTH.title,

      ...columns
        .filter((it) => it.enabled)
        .map((it) => DYNAMIC_COLUMNS_WIDTH[it.key]),

      STATIC_COLUMNS_WIDTH.rating,
      STATIC_COLUMNS_WIDTH.actions,
    ].join(' '),
  };
}

const STATIC_COLUMNS_WIDTH = {
  image: '100px',
  title: 'minmax(100px, auto)',
  rating: '190px',
  actions: '4rem',
} as const;

const DYNAMIC_COLUMNS_WIDTH: Record<ProjectListColumnKey, string | null> = {
  [ProjectListColumnKey.MEMBERS]: 'minmax(75px, 7.5rem)',
  [ProjectListColumnKey.STATUS]: 'minmax(100px, 150px)',
  [ProjectListColumnKey.UPDATED]: 'minmax(100px, 150px)',
  [ProjectListColumnKey.ROI]: 'minmax(100px, 165px)',
  [ProjectListColumnKey.ROI_2]: 'minmax(100px, 150px)',
  [ProjectListColumnKey.ROI_5]: 'minmax(100px, 150px)',
  [ProjectListColumnKey.BEP]: 'minmax(100px, 150px)',
};
